/**
 * Lunchy — moteur de synchronisation couleur ↔ visuels ↔ variante.
 *
 * Une seule source de vérité (LunchyStore) pilote les 3 endroits où l'on
 * peut choisir une couleur (hero, sélecteur pleine largeur, bloc commande
 * final) + la galerie principale + le prix/stock/CTA + la barre sticky
 * mobile + l'URL. Tout est fait en mémoire à partir du JSON déjà présent
 * dans la page (product.variants / product.media) : aucun fetch réseau,
 * donc bascule instantanée, sans flash.
 */
(function () {
  'use strict';

  function normalizeColorKey(value) {
    if (!value) return '';
    var v = value
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');
    if (/mint|vert\s*menthe/.test(v)) return 'mint';
    if (/cream|creme|beige/.test(v)) return 'cream';
    return v.replace(/[^a-z0-9]+/g, '-');
  }

  function readJSON(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function formatMoney(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function' && window.lunchyMoneyFormat) {
      return Shopify.formatMoney(cents, window.lunchyMoneyFormat);
    }
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }

  var LunchyStore = {
    variants: [],
    bundleVariants: [],
    media: [],
    mediaByColor: {},
    genericMedia: [],
    currentColorKey: null,
    currentVariantId: null,
    currentTier: 'bundle',
    listeners: [],
    token: 0,

    init: function () {
      this.variants = readJSON('lunchy-variants-data') || [];
      this.bundleVariants = readJSON('lunchy-bundle-variants-data') || [];
      if (!this.bundleVariants.length) this.currentTier = 'solo';
      this.media = readJSON('lunchy-media-data') || [];
      window.lunchyMoneyFormat = window.lunchyMoneyFormat || (document.getElementById('lunchy-money-format') || {}).textContent;

      var self = this;
      this.media.forEach(function (m) {
        var key = null;
        var altPrefix = (m.alt || '').split(/[—\-:]/)[0];
        if (/mint|vert\s*menthe/i.test(altPrefix)) key = 'mint';
        else if (/cream|creme|beige/i.test(altPrefix)) key = 'cream';
        if (key) {
          self.mediaByColor[key] = self.mediaByColor[key] || [];
          self.mediaByColor[key].push(m);
        } else {
          self.genericMedia.push(m);
        }
      });

      // Variante initiale : paramètre d'URL ?variant=, sinon 1ère dispo, sinon 1ère.
      var params = new URLSearchParams(window.location.search);
      var urlVariant = params.get('variant');
      var initial =
        this.variants.find(function (v) { return String(v.id) === urlVariant; }) ||
        this.variants.find(function (v) { return v.available; }) ||
        this.variants[0];

      if (initial) {
        this.currentVariantId = initial.id;
        this.currentColorKey = normalizeColorKey(initial.option1);
      }
    },

    getVariant: function (id) {
      return this.variants.find(function (v) { return v.id === id; });
    },

    getVariantByColorKey: function (key) {
      return this.variants.find(function (v) { return normalizeColorKey(v.option1) === key; });
    },

    getBundleVariantByColorKey: function (key) {
      return this.bundleVariants.find(function (v) { return normalizeColorKey(v.option1) === key; });
    },

    getActiveVariant: function (colorKey) {
      if (this.currentTier === 'bundle') {
        return this.getBundleVariantByColorKey(colorKey) || this.getVariantByColorKey(colorKey);
      }
      return this.getVariantByColorKey(colorKey);
    },

    getGalleryForColor: function (colorKey, anchorMediaId) {
      var list = (this.mediaByColor[colorKey] || []).concat(this.genericMedia);
      if (!list.length && this.media.length) list = this.media.slice();
      if (anchorMediaId) {
        list = list.slice().sort(function (a, b) {
          if (a.id === anchorMediaId) return -1;
          if (b.id === anchorMediaId) return 1;
          return (a.position || 0) - (b.position || 0);
        });
      }
      return list;
    },

    subscribe: function (fn) {
      this.listeners.push(fn);
    },

    select: function (colorKey, opts) {
      opts = opts || {};
      // Toujours ancrée sur la variante "solo" : c'est elle qui porte la
      // vraie galerie du produit. La variante active (celle réellement
      // ajoutée au panier) peut être la variante du Kit si ce tier est
      // sélectionné — cf. getActiveVariant.
      var soloVariant = this.getVariantByColorKey(colorKey);
      if (!soloVariant) return;
      var activeVariant = this.getActiveVariant(colorKey) || soloVariant;

      this.currentColorKey = colorKey;
      this.currentVariantId = activeVariant.id;
      this.token += 1;
      var myToken = this.token;

      if (!opts.skipHistory && window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', activeVariant.id);
        window.history.replaceState({}, '', url);
      }

      var anchorId = soloVariant.featured_media ? soloVariant.featured_media.id : null;
      var gallery = this.getGalleryForColor(colorKey, anchorId);

      this.listeners.forEach(function (fn) {
        fn({ variant: activeVariant, colorKey: colorKey, gallery: gallery, token: myToken });
      });
    },

    selectTier: function (tier) {
      if (tier !== 'solo' && tier !== 'bundle') return;
      this.currentTier = tier;
      this.tierListeners.forEach(function (fn) { fn(tier); });
      if (this.currentColorKey) this.select(this.currentColorKey);
    },

    tierListeners: [],
    subscribeTier: function (fn) {
      this.tierListeners.push(fn);
    }
  };

  /* ------------------------------------------------------------------ *
   * Swatches (role="radio" dans un role="radiogroup") — 3 instances.
   * ------------------------------------------------------------------ */
  function wireSwatchGroups() {
    var groups = document.querySelectorAll('[data-lunchy-swatch-group]');
    groups.forEach(function (group) {
      var buttons = Array.prototype.slice.call(group.querySelectorAll('[data-lunchy-swatch]'));

      buttons.forEach(function (btn, i) {
        btn.addEventListener('click', function () {
          LunchyStore.select(btn.getAttribute('data-color-key'));
        });
        btn.addEventListener('keydown', function (e) {
          var dir = 0;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') dir = 1;
          if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') dir = -1;
          if (!dir) return;
          e.preventDefault();
          var next = buttons[(i + dir + buttons.length) % buttons.length];
          next.focus();
          LunchyStore.select(next.getAttribute('data-color-key'));
        });
      });

      LunchyStore.subscribe(function (state) {
        buttons.forEach(function (btn) {
          var isActive = btn.getAttribute('data-color-key') === state.colorKey;
          btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
          btn.setAttribute('tabindex', isActive ? '0' : '-1');
        });
        var label = group.parentElement && group.parentElement.querySelector('[data-lunchy-swatch-current]');
        if (label) {
          var active = buttons.filter(function (b) { return b.getAttribute('data-color-key') === state.colorKey; })[0];
          if (active) label.textContent = active.getAttribute('data-color-label') || '';
        }
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Sélecteur d'offre (Solo / Kit complet) — 2 cartes, role="radio".
   * Change uniquement QUEL variant est actif (solo ou kit) ; la couleur
   * choisie juste en dessous reste indépendante et s'applique aux deux.
   * ------------------------------------------------------------------ */
  function wireTierSelector() {
    var groups = document.querySelectorAll('[data-lunchy-tier-group]');
    if (!groups.length) return;

    groups.forEach(function (group) {
      var cards = Array.prototype.slice.call(group.querySelectorAll('[data-lunchy-tier]'));

      cards.forEach(function (card, i) {
        card.addEventListener('click', function () {
          LunchyStore.selectTier(card.getAttribute('data-lunchy-tier'));
        });
        card.addEventListener('keydown', function (e) {
          var dir = 0;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') dir = 1;
          if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') dir = -1;
          if (!dir) return;
          e.preventDefault();
          var next = cards[(i + dir + cards.length) % cards.length];
          next.focus();
          LunchyStore.selectTier(next.getAttribute('data-lunchy-tier'));
        });
      });

      function render(tier) {
        cards.forEach(function (card) {
          var isActive = card.getAttribute('data-lunchy-tier') === tier;
          card.setAttribute('aria-checked', isActive ? 'true' : 'false');
          card.setAttribute('tabindex', isActive ? '0' : '-1');
          card.classList.toggle('is-active', isActive);
        });
      }

      render(LunchyStore.currentTier);
      LunchyStore.subscribeTier(render);
    });
  }

  /* ------------------------------------------------------------------ *
   * Cartes coloris pleine largeur (section 7) — même logique, rôle radio.
   * ------------------------------------------------------------------ */
  function wireColorCards() {
    var cards = document.querySelectorAll('[data-lunchy-color-card]');
    if (!cards.length) return;
    cards.forEach(function (card) {
      card.addEventListener('click', function () {
        LunchyStore.select(card.getAttribute('data-color-key'));
      });
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          LunchyStore.select(card.getAttribute('data-color-key'));
        }
      });
    });
    LunchyStore.subscribe(function (state) {
      cards.forEach(function (card) {
        var isActive = card.getAttribute('data-color-key') === state.colorKey;
        card.setAttribute('aria-checked', isActive ? 'true' : 'false');
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Galerie principale (hero) — cross-fade, sans latence, sans coupure.
   * ------------------------------------------------------------------ */
  function wireGallery() {
    var mainWraps = document.querySelectorAll('[data-lunchy-gallery-main]');
    if (!mainWraps.length) return;

    mainWraps.forEach(function (wrap) {
      var thumbsWrap = wrap.parentElement.querySelector('[data-lunchy-gallery-thumbs]');

      function renderThumbActive(activeId) {
        if (!thumbsWrap) return;
        thumbsWrap.querySelectorAll('[data-media-id]').forEach(function (t) {
          t.classList.toggle('is-active', t.getAttribute('data-media-id') === String(activeId));
        });
      }

      function showImage(mediaId) {
        var target = wrap.querySelector('img[data-media-id="' + CSS.escape(String(mediaId)) + '"]');
        if (!target) return;
        wrap.querySelectorAll('img').forEach(function (img) {
          if (img !== target) img.classList.remove('is-active');
        });
        // force reflow so the opacity transition always runs, even if the
        // browser had already marked the image active in a previous cycle.
        void target.offsetWidth;
        target.classList.add('is-active');
        renderThumbActive(mediaId);
      }

      if (thumbsWrap) {
        thumbsWrap.addEventListener('click', function (e) {
          var thumb = e.target.closest('[data-media-id]');
          if (!thumb) return;
          showImage(thumb.getAttribute('data-media-id'));
        });
      }

      LunchyStore.subscribe(function (state) {
        if (state.token !== LunchyStore.token) return; // le dernier clic gagne
        if (!state.gallery.length) return;
        showImage(state.gallery[0].id);
      });

      wrap.__lunchyShowImage = showImage;
    });
  }

  /* ------------------------------------------------------------------ *
   * Prix / stock / CTA / input caché variant_id — hero + bloc commande.
   * ------------------------------------------------------------------ */
  function wireBuyBlocks() {
    var blocks = document.querySelectorAll('[data-lunchy-buy-block]');

    var lowStockThreshold = 5;
    var thresholdEl = document.querySelector('[data-low-stock-threshold]');
    if (thresholdEl) {
      var parsed = parseInt(thresholdEl.getAttribute('data-low-stock-threshold'), 10);
      if (!isNaN(parsed) && parsed > 0) lowStockThreshold = parsed;
    }

    blocks.forEach(function (block) {
      var priceEl = block.querySelector('[data-lunchy-price]');
      var comparePriceEl = block.querySelector('[data-lunchy-compare-price]');
      var badgeEl = block.querySelector('[data-lunchy-badge]');
      var availEl = block.querySelector('[data-lunchy-availability]');
      var idInput = block.querySelector('[data-lunchy-variant-id-input]');
      var ctaBtn = block.querySelector('[data-lunchy-add-to-cart]');
      var ctaLabel = ctaBtn ? ctaBtn.querySelector('[data-lunchy-cta-label]') : null;

      LunchyStore.subscribe(function (state) {
        var v = state.variant;
        if (idInput) idInput.value = v.id;
        if (priceEl) priceEl.textContent = formatMoney(v.price);
        if (comparePriceEl) {
          if (v.compare_at_price && v.compare_at_price > v.price) {
            comparePriceEl.textContent = formatMoney(v.compare_at_price);
            comparePriceEl.hidden = false;
            if (badgeEl) {
              badgeEl.textContent = '-' + formatMoney(v.compare_at_price - v.price);
              badgeEl.hidden = false;
            }
          } else {
            comparePriceEl.hidden = true;
            if (badgeEl) badgeEl.hidden = true;
          }
        }
        if (availEl) {
          var isLowStock =
            v.available &&
            v.inventory_management === 'shopify' &&
            typeof v.inventory_quantity === 'number' &&
            v.inventory_quantity > 0 &&
            v.inventory_quantity <= lowStockThreshold;

          if (!v.available) {
            availEl.textContent = availEl.getAttribute('data-out-stock-text');
          } else if (isLowStock) {
            availEl.textContent = (availEl.getAttribute('data-low-stock-text') || '').replace('{qty}', v.inventory_quantity);
          } else {
            availEl.textContent = availEl.getAttribute('data-in-stock-text');
          }
          availEl.classList.toggle('lunchy-availability--in', v.available);
          availEl.classList.toggle('lunchy-availability--out', !v.available);
          availEl.classList.toggle('lunchy-availability--low', isLowStock);
        }
        if (ctaBtn) {
          ctaBtn.disabled = !v.available;
          if (ctaLabel) ctaLabel.textContent = v.available ? ctaLabel.getAttribute('data-default-text') : ctaLabel.getAttribute('data-sold-out-text');
        }
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Petites vignettes annexes (bloc commande, barre sticky) — suivent
   * simplement la 1ère image de la galerie du coloris sélectionné.
   * ------------------------------------------------------------------ */
  function wireSimpleThumbs() {
    var thumbs = document.querySelectorAll('[data-lunchy-simple-thumb]');
    if (!thumbs.length) return;
    LunchyStore.subscribe(function (state) {
      if (!state.gallery.length) return;
      var src = state.gallery[0].src || state.gallery[0].thumb;
      thumbs.forEach(function (img) { img.src = src; });
    });
  }

  /* ------------------------------------------------------------------ *
   * Ajout panier (fetch, sans rechargement) + mise à jour bulle panier.
   * ------------------------------------------------------------------ */
  function updateCartBubble() {
    // Dawn ne rend la pastille (.cart-count-bubble) que si le panier
    // n'était PAS vide au chargement de la page ({% if cart != empty %}
    // dans sections/cart-icon-bubble.liquid) : après un tout premier ajout
    // sans rechargement, l'élément n'existe simplement pas encore dans le
    // DOM, donc le re-remplir ne suffit pas — il faut re-render la section
    // via l'API Shopify et remplacer le conteneur entier.
    fetch('/?sections=cart-icon-bubble')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data['cart-icon-bubble'];
        var wrapper = document.getElementById('cart-icon-bubble');
        if (html && wrapper) wrapper.innerHTML = html;
      })
      .catch(function () {});

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-lunchy-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
        });
      })
      .catch(function () {});
  }

  function showToast(message) {
    var toast = document.querySelector('[data-lunchy-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(toast.__timer);
    toast.__timer = setTimeout(function () {
      toast.classList.remove('is-visible');
    }, 2600);
  }

  function wireAddToCart() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-lunchy-add-to-cart]');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      var block = btn.closest('[data-lunchy-buy-block]');
      if (!block) return;
      var idInput = block.querySelector('[data-lunchy-variant-id-input]');
      var qtyInput = block.querySelector('[data-lunchy-quantity-input]');
      if (!idInput || !idInput.value) return;

      var quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      btn.disabled = true;
      var label = btn.querySelector('[data-lunchy-cta-label]');
      var originalText = label ? label.textContent : null;
      if (label) label.textContent = label.getAttribute('data-loading-text') || '…';

      // Toujours relatif : Shopify.routes.cart_add_url n'existe pas dans ce
      // thème (ce n'est pas un objet garanti par Dawn), un thème qui s'y
      // fiait laissait le bouton "Ajouter au panier" silencieusement mort.
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: idInput.value, quantity: quantity })
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data.status) throw new Error(data.description || 'Erreur');
          showToast(btn.getAttribute('data-added-text') || 'Ajouté au panier ✓');
          updateCartBubble();
        })
        .catch(function () {
          showToast(btn.getAttribute('data-error-text') || "Une erreur est survenue, réessayez.");
        })
        .finally(function () {
          btn.disabled = false;
          if (label && originalText) label.textContent = originalText;
        });
    });

    document.addEventListener('click', function (e) {
      var minus = e.target.closest('[data-lunchy-qty-minus]');
      var plus = e.target.closest('[data-lunchy-qty-plus]');
      if (!minus && !plus) return;
      var wrap = (minus || plus).closest('.lunchy-quantity');
      var input = wrap && wrap.querySelector('[data-lunchy-quantity-input]');
      if (!input) return;
      var val = parseInt(input.value, 10) || 1;
      val = plus ? val + 1 : Math.max(1, val - 1);
      input.value = val;
    });
  }

  /* ------------------------------------------------------------------ *
   * Compte à rebours d'offre (réel, jamais fabriqué) — une seule source
   * (le Hero) mirrorée dans le bloc commande final. Disparaît proprement
   * si la date n'est pas renseignée / mal formée / déjà passée : on
   * n'affiche jamais un compte à rebours cassé ou à zéro.
   * ------------------------------------------------------------------ */
  function wireCountdown() {
    var master = document.querySelector('[data-lunchy-countdown]');
    var mirrors = document.querySelectorAll('[data-lunchy-countdown-mirror]');
    if (!master) return;

    var endStr = (master.getAttribute('data-end') || '').trim();
    var end = endStr ? new Date(endStr.replace(' ', 'T')) : null;
    var label = master.getAttribute('data-label') || '';

    function hideAll() {
      master.hidden = true;
      mirrors.forEach(function (m) { m.hidden = true; });
    }

    if (!end || isNaN(end.getTime()) || end.getTime() <= Date.now()) {
      hideAll();
      return;
    }

    function pad(n) { return String(n).padStart(2, '0'); }

    function render() {
      var diff = end.getTime() - Date.now();
      if (diff <= 0) {
        hideAll();
        clearInterval(timer);
        return;
      }
      var d = Math.floor(diff / 86400000);
      var h = Math.floor((diff % 86400000) / 3600000);
      var m = Math.floor((diff % 3600000) / 60000);
      var s = Math.floor((diff % 60000) / 1000);
      var parts = [];
      if (d > 0) parts.push(d + 'j');
      parts.push(pad(h) + 'h');
      parts.push(pad(m) + 'm');
      parts.push(pad(s) + 's');

      var html =
        '<span class="lunchy-countdown__label">' + label + '</span>' +
        '<span class="lunchy-countdown__time">' + parts.join(' ') + '</span>';

      master.innerHTML = html;
      master.hidden = false;
      mirrors.forEach(function (m) {
        m.innerHTML = html;
        m.hidden = false;
      });
    }

    render();
    var timer = setInterval(render, 1000);
  }

  /* ------------------------------------------------------------------ *
   * Barre sticky mobile.
   * ------------------------------------------------------------------ */
  function wireStickyBar() {
    var bar = document.querySelector('[data-lunchy-sticky-bar]');
    var hero = document.querySelector('[data-lunchy-hero]');
    if (!bar || !hero) return;

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
      },
      { threshold: 0 }
    );
    io.observe(hero);
  }

  /* ------------------------------------------------------------------ *
   * Reveal on scroll (une seule fois, discret).
   * ------------------------------------------------------------------ */
  function wireReveal() {
    var els = document.querySelectorAll('.lunchy-reveal');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach(function (el) { io.observe(el); });
  }

  /* ------------------------------------------------------------------ *
   * FAQ — accordéon : un seul <details> ouvert à la fois (confort visuel).
   * ------------------------------------------------------------------ */
  function wireFaq() {
    var items = document.querySelectorAll('.lunchy-faq-item');
    items.forEach(function (item) {
      item.addEventListener('toggle', function () {
        if (!item.open) return;
        items.forEach(function (other) {
          if (other !== item) other.open = false;
        });
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Parallax hero très léger (desktop, pointer fin, motion non réduit).
   * ------------------------------------------------------------------ */
  function wireParallax() {
    var media = document.querySelector('[data-lunchy-parallax]');
    if (!media) return;
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (reduceMotion || !finePointer) return;

    function update() {
      var rect = media.getBoundingClientRect();
      var offset = Math.max(-20, Math.min(20, rect.top * 0.06));
      media.style.transform = 'translateY(' + offset + 'px)';
    }
    // Appel direct (pas de garde requestAnimationFrame) : un flag
    // "ticking" qui attend un rAF peut rester bloqué indéfiniment si la
    // page est en arrière-plan au moment du scroll (rAF suspendu par le
    // navigateur), ce qui gèlerait l'effet pour de bon. Le calcul est
    // assez léger pour tourner directement sur l'event.
    window.addEventListener('scroll', update, { passive: true });
  }

  /* ------------------------------------------------------------------ *
   * Vidéo démo — autoplay muted visible desktop, clic pour lancer + son.
   * ------------------------------------------------------------------ */
  function wireVideo() {
    var frame = document.querySelector('[data-lunchy-video-frame]');
    if (!frame) return;
    var video = frame.querySelector('video');
    var playBtn = frame.querySelector('[data-lunchy-video-play]');
    if (!video) return;

    if (playBtn) {
      playBtn.addEventListener('click', function () {
        video.muted = false;
        video.controls = true;
        video.play();
        frame.classList.add('is-playing');
      });
    }

    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            video.play().catch(function () {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.5 }
    );
    io.observe(frame);
  }

  /* ------------------------------------------------------------------ *
   * Vue éclatée pilotée par le scroll — la molette "scrube" une vidéo
   * déjà chargée (video.currentTime), sans jamais la laisser jouer
   * toute seule. Désactivé si l'utilisateur préfère moins d'animations
   * (voir le fallback statique dans lunchy-theme.css).
   * ------------------------------------------------------------------ */
  function wireExplodedScroll() {
    var section = document.querySelector('[data-lunchy-exploded]');
    if (!section) return;
    // Pas de scrub sur mobile/tactile : Safari iOS ne décode pas de façon
    // fiable une frame vidéo arbitraire sans lecture réelle, ce qui
    // affichait un rectangle gris cassé au lieu de l'animation — la
    // section repasse en image fixe via lunchy-theme.css dans ce cas, le
    // JS ne doit donc pas non plus tenter de piloter video.currentTime.
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var isMobileOrTouch = window.matchMedia('(max-width: 749px), (hover: none)').matches;
    if (reduceMotion || isMobileOrTouch) return;

    var video = section.querySelector('[data-lunchy-exploded-video]');
    var progressBar = section.querySelector('[data-lunchy-exploded-progress]');
    if (!video) return;

    // La balise part en preload="none" (pour ne jamais télécharger cette
    // vidéo sur mobile, où elle ne sert de toute façon jamais) : ici on
    // sait qu'on est sur desktop et qu'on va vraiment scruber, donc on
    // déclenche le chargement explicitement.
    video.preload = 'auto';
    video.load();
    video.pause();

    var duration = 0;
    var ready = false;

    function onLoadedMeta() {
      duration = video.duration || 0;
      ready = duration > 0;
      update();
    }
    if (video.readyState >= 1 && video.duration) {
      onLoadedMeta();
    } else {
      video.addEventListener('loadedmetadata', onLoadedMeta, { once: true });
    }

    function update() {
      if (!ready) return;
      var rect = section.getBoundingClientRect();
      var scrollable = rect.height - window.innerHeight;
      if (scrollable <= 0) return;
      var progress = -rect.top / scrollable;
      progress = Math.max(0, Math.min(1, progress));

      var target = progress * duration;
      if (Math.abs(video.currentTime - target) > 0.02) {
        try {
          video.currentTime = target;
        } catch (e) {
          /* certains navigateurs jettent une erreur si le seek arrive
             avant que les métadonnées soient vraiment prêtes — sans
             conséquence, la frame se met à jour au tick suivant */
        }
      }
      if (progressBar) progressBar.style.width = (progress * 100) + '%';
    }

    // Appel direct sur l'event scroll, sans garde requestAnimationFrame :
    // un flag qui attend un rAF peut rester bloqué si la page est en
    // arrière-plan pile au moment du scroll (rAF suspendu par le
    // navigateur), ce qui gèlerait le scrub pour de bon. Le calcul
    // (un getBoundingClientRect + une comparaison) est assez léger.
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  document.addEventListener('DOMContentLoaded', function () {
    LunchyStore.init();
    wireTierSelector();
    wireSwatchGroups();
    wireColorCards();
    wireGallery();
    wireBuyBlocks();
    wireSimpleThumbs();
    wireAddToCart();
    wireStickyBar();
    wireCountdown();
    wireReveal();
    wireFaq();
    wireParallax();
    wireVideo();
    wireExplodedScroll();

    if (LunchyStore.currentColorKey) {
      LunchyStore.select(LunchyStore.currentColorKey, { skipHistory: true });
    }
  });
})();
